// src/pages/ConsultantMeetings.js
// Team selector — shows all teams the consultant is assigned to.
// Clicking a team goes to ConsultantTeamHub (/meetings/:teamId)
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { collection, query, where, getDocs } from 'firebase/firestore/lite';
import { db } from '../firebase/config';
import { useAuth } from '../context/AuthContext';
import styles from './ConsultantMeetings.module.css';
import PageLoading from '../components/PageLoading';

export default function ConsultantMeetings() {
  const { currentUser, userProfile } = useAuth();
  const navigate = useNavigate();
  const [teams, setTeams]           = useState([]);
  const [leaderNames, setLeaderNames] = useState({});
  const [loading, setLoading]       = useState(true);

  useEffect(() => { loadTeams(); }, [userProfile]);

  async function loadTeams() {
    try {
      const snap = await getDocs(
        query(
          collection(db, 'teams'),
          where('consultantEmails', 'array-contains', userProfile?.email || currentUser.email)
        )
      );
      const teamList = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      setTeams(teamList);

      // Look up display names for all team leaders
      const emails = [...new Set(teamList.map(t => t.leaderEmail).filter(Boolean))];
      if (emails.length > 0) {
        const usersSnap = await getDocs(
          query(collection(db, 'users'), where('email', 'in', emails))
        );
        const nameMap = {};
        usersSnap.docs.forEach(d => {
          const { email, displayName } = d.data();
          if (email) nameMap[email] = displayName || email;
        });
        setLeaderNames(nameMap);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }

  if (loading) return <PageLoading text="Loading your teams..." />;

  return (
    <div className="fade-up">
      <div className="page-header">
        <h2>My Teams</h2>
        <p>Select a team to view meetings, submit an assessment, or review results.</p>
      </div>

      {teams.length === 0 ? (
        <div className={`card ${styles.emptyCard}`}>
          <div className={styles.emptyIcon}>👥</div>
          <h3 className={styles.emptyTitle}>No teams yet</h3>
          <p className={styles.emptyText}>
            You'll appear here once a team leader invites you as a consultant.
          </p>
        </div>
      ) : (
        <div className={styles.teamList}>
          {teams.map(team => (
            <button
              key={team.id}
              onClick={() => navigate(`/meetings/${team.id}`)}
              className={styles.teamCard}
            >
              <div className={styles.teamCardBody}>
                <div className={styles.teamCardTop}>
                  <div className={styles.teamAvatar}>
                    {team.name?.charAt(0) || '?'}
                  </div>
                  <div>
                    <div className={styles.teamName}>{team.name}</div>
                    <div className={styles.teamMeta}>
                      {team.memberEmails?.length || 0} members
                      {team.leaderEmail && ` · Led by ${leaderNames[team.leaderEmail] || team.leaderEmail}`}
                    </div>
                  </div>
                </div>
                <div className={styles.teamCardBadges}>
                  <div className={styles.badgeGroup}>
                    <div className={styles.badgeLabel}>Meetings</div>
                    <span className="badge badge-blue">View</span>
                  </div>
                  <div className={styles.badgeGroup}>
                    <div className={styles.badgeLabel}>Assessment</div>
                    <span className="badge badge-green">View</span>
                  </div>
                  <div className={styles.badgeGroup}>
                    <div className={styles.badgeLabel}>Results</div>
                    <span className="badge badge-purple">View</span>
                  </div>
                </div>
              </div>
              <span className={styles.teamChevron}>›</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
